import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, X } from "lucide-react";
import { useLanguage } from "../i18n";
import { canPromptInstall, isStandaloneApp, promptInstall, subscribeInstall } from "../utils/pwa";

const DISMISS_KEY = "simamma_pwa_dismiss_until";
const DELAY_MS = 800;  // quasi immediato all'apertura del sito (lascia solo il primo render)
const COOLDOWN_DAYS = 7;

// Popup di installazione PWA: solo mobile, solo browser (mai dentro l'app),
// una volta ogni 7 giorni se chiuso, con il sito visibile sotto in trasparenza.
export default function InstallAppPopup() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [, force] = useState(0);
  useEffect(() => subscribeInstall(() => force((n) => n + 1)), []);

  useEffect(() => {
    if (isStandaloneApp()) return undefined;
    if (location.pathname.startsWith("/admin") || location.pathname === "/app") return undefined;
    if (!window.matchMedia("(max-width: 768px)").matches) return undefined;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < until) return undefined;
    const timer = setTimeout(() => setShow(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_DAYS * 86400000));
    setShow(false);
  };

  const install = async () => {
    if (canPromptInstall() && (await promptInstall())) {
      setShow(false);
      return;
    }
    // niente prompt nativo (iOS/Safari): la pagina /app guida l'installazione
    setShow(false);
    navigate("/app");
  };

  return (
    <div data-testid="pwa-install-popup" className="fixed inset-x-3 bottom-3 z-[70] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px]">
      <style>{`@keyframes pwaSlideUp { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
      <div className="rounded-2xl bg-[#1E293B] text-white p-3.5 shadow-2xl border border-white/10 flex items-center gap-3"
        style={{ animation: "pwaSlideUp .3s ease-out" }}>
        <img src="/icons/icon-192.png" alt="SIMAMMA" className="w-10 h-10 rounded-xl shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display font-extrabold text-sm leading-tight">{t("pwa.popT")}</div>
          <div className="text-[11px] text-slate-300 leading-snug line-clamp-2 mt-0.5">{t("pwa.popD")}</div>
        </div>
        <button data-testid="pwa-popup-install" onClick={install}
          className="shrink-0 rounded-full bg-[#FF6B35] text-white px-4 min-h-[44px] font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> {t("appPage.install")}
        </button>
        <button data-testid="pwa-popup-close" onClick={dismiss} aria-label="chiudi"
          className="shrink-0 -mr-1.5 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
